import { useState, useEffect } from 'react';
import { ArrowLeft, Share2, Heart, ExternalLink, Eye, EyeOff, Info, Trophy } from 'lucide-react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { PhotoGallery } from '../components/PhotoGallery';
import { useCurrency } from '../hooks/useCurrency';
import { Timestamp } from 'firebase/firestore';
import { doc, updateDoc, increment, getDoc, setDoc, deleteDoc, collection, query, where, onSnapshot, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../hooks/useAuth';
import { getUserRanking } from '../config/firebase';
import { formatAmount } from '../utils/formatters';
import { ShareModal } from '../components/ShareModal';
import { generateInitialsAvatar } from '../utils/avatarUtils';
import React from 'react';

interface Photo {
  url: string;
  title: string;
  description: string;
}

interface SocialLink {
  platform: string;
  url: string;
}

interface ProfileData {
  userId: string;
  username: string;
  description?: string;
  amountGiven: number;
  likes: number;
  socialLinks?: Array<{
    platform: string;
    url: string;
  }>;
  photos?: Array<{
    id: string;
    url: string;
    title?: string;
    description?: string;
  }>;
  donations: Array<{
    amount: number;
    date: string;
    requestId: string;
    requestTitle: string;
  }>;
  received: Array<{
    amount: number;
    date: string;
    from: string;
    requestTitle: string;
  }>;
  usernameHistory?: Array<{
    username: string;
    changedAt: Timestamp | Date;
  }>;
  showExternalPhotos: boolean;
  externalPhotos: Array<string>;
  profilePicture?: string;
}

interface Donation {
  id: string;
  requestId: string;
  requestTitle: string;
  amount: number;
  date: string | number;
  userId?: string;
  isAnonymous: boolean;
}

interface GroupedDonation {
  requestId: string;
  requestTitle: string;
  totalAmount: number;
  donations: Array<{
    amount: number;
    date: Date;
    userId: string;
  }>;
}

interface Request {
  id: string;
  title?: string;
  details?: string;
  donations?: Array<{
    amount: number;
    date: Timestamp | string;
    userId: string;
  }>;
}

export function PublicProfile() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [isLiked, setIsLiked] = useState(false);
  const [profileData, setProfileData] = useState<ProfileData | null>(location.state as ProfileData | null);
  const currency = useCurrency();
  const [totalLikes, setTotalLikes] = useState(0);
  const { userId } = useParams();
  const [receivedDonations, setReceivedDonations] = useState<GroupedDonation[]>([]);
  const [userRanking, setUserRanking] = useState<number | null>(null);
  const [sentDonations, setSentDonations] = useState<Donation[]>([]);
  const [donationNotifications, setDonationNotifications] = useState<any[]>([]);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const showAuthPrompt = !user;

  const fetchUserProfile = async () => {
    const targetUserId = userId;
    if (!targetUserId) return;
    
    try {
      const [userDoc, profileSettingsDoc] = await Promise.all([
        getDoc(doc(db, 'users', targetUserId)),
        getDoc(doc(db, 'profileSettings', targetUserId))
      ]);

      if (userDoc.exists()) {
        const userData = userDoc.data();
        const settingsData = profileSettingsDoc.exists() ? profileSettingsDoc.data() : {};

        // S'assurer que le nom d'utilisateur existe
        const username = userData.username || '';
        
        // Générer l'avatar par défaut
        const defaultAvatar = generateInitialsAvatar(username);
        
        // Utiliser la photo de profil existante ou l'avatar par défaut
        const profilePicture = userData.profilePicture || settingsData.profilePicture || defaultAvatar;

        // Récupérer les photos externes depuis localStorage si disponible
        let externalPhotos = [];
        try {
          const savedPhotos = localStorage.getItem(`user_${targetUserId}_photos`);
          if (savedPhotos) {
            const parsedPhotos = JSON.parse(savedPhotos);
            externalPhotos = parsedPhotos.externalPhotos || [];
          } else if (settingsData.externalPhotos) {
            // Sinon, utiliser les photos de Firestore
            externalPhotos = settingsData.externalPhotos;
          }
        } catch (error) {
          console.error('Erreur lors de la récupération des photos:', error);
        }

        setProfileData({
          ...profileData,
          userId: targetUserId,
          username: username,
          description: userData.description || '',
          socialLinks: userData.socialLinks || [],
          profilePicture: profilePicture,
          amountGiven: userData.amountGiven || 0,
          likes: userData.likes || 0,
          showExternalPhotos: userData.showExternalPhotos || settingsData.showExternalPhotos || false,
          externalPhotos: externalPhotos,
          donations: userData.donations || [],
          received: userData.received || []
        });
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du profil:', error);
    }
  };

  useEffect(() => {
    fetchUserProfile();
  }, [userId]);

  useEffect(() => {
    if (profileData?.userId) {
      const getLikes = async () => {
        const userRef = doc(db, 'users', profileData.userId);
        const userDoc = await getDoc(userRef);
        setTotalLikes(userDoc.data()?.likes || 0);
      };
      
      const checkUserLike = async () => {
        if (user?.id) {
          const likeRef = doc(db, 'users', profileData.userId, 'profileLikes', user.id);
          const likeDoc = await getDoc(likeRef);
          setIsLiked(likeDoc.exists());
        }
      };
      
      getLikes();
      checkUserLike();
    }
  }, [profileData?.userId, user?.id]);

  useEffect(() => {
    if (!profileData?.userId) return;

    // Écouter les donations envoyées
    const sentQuery = query(
      collection(db, 'recentDonations'),
      where('userId', '==', profileData.userId),
      where('is_public', '==', true)
    );

    const unsubscribeSent = onSnapshot(sentQuery, (snapshot) => {
      const donations: Donation[] = [];
      let totalAmount = 0;
      
      snapshot.forEach((doc) => {
        const donation = { id: doc.id, ...doc.data() } as Donation;
        donations.push(donation);
        totalAmount += donation.amount;
      });

      setSentDonations(donations);
      setProfileData(prev => prev ? {
        ...prev,
        amountGiven: totalAmount
      } : null);
    });

    // Écouter les donations reçues
    const requestsQuery = query(
      collection(db, '2dr_requests'),
      where('userId', '==', profileData.userId)
    );

    const unsubscribe = onSnapshot(requestsQuery, async (snapshot) => {
      const requests = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const groupedDonations = requests.reduce<GroupedDonation[]>((acc, request: Request) => {
        if (!request.donations || request.donations.length === 0) return acc;

        const totalAmount = request.donations.reduce((sum: number, donation) => sum + donation.amount, 0);

        if (totalAmount > 0) {
          acc.push({
            requestId: request.id,
            requestTitle: request.title || request.details || 'Sans titre',
            totalAmount,
            donations: request.donations.map(d => ({
              amount: d.amount,
              date: d.date instanceof Timestamp ? d.date.toDate() : new Date(d.date),
              userId: d.userId
            }))
          });
        }

        return acc;
      }, []);

      setReceivedDonations(groupedDonations);
    });

    return () => {
      unsubscribeSent();
      unsubscribe();
    };
  }, [profileData?.userId]);

  useEffect(() => {
    if (!profileData?.userId) return;
    
    const fetchRanking = async () => {
      const ranking = await getUserRanking(profileData.userId);
      setUserRanking(ranking);
    };

    fetchRanking();
  }, [profileData?.userId, profileData?.amountGiven]);

  useEffect(() => {
    if (!userId) return;
    
    const unsubscribe = onSnapshot(doc(db, 'users', userId), (doc) => {
      if (doc.exists()) {
        // Recharger les données du profil
        fetchUserProfile();
      }
    });

    return () => unsubscribe();
  }, [userId]);

  useEffect(() => {
    if (!profileData?.userId) return;

    // Écouter les notifications de donations envoyées
    const notificationsQuery = query(
      collection(db, 'notifications'),
      where('userId', '==', profileData.userId),
      where('type', '==', 'donation_sent'),
      where('status', '==', 'unread'),
      orderBy('createdAt', 'desc')
    );

    const unsubscribeNotifications = onSnapshot(notificationsQuery, (snapshot) => {
      const notifications = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setDonationNotifications(notifications);
    });

    return () => {
      unsubscribeNotifications();
    };
  }, [profileData?.userId]);

  if (!profileData) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-400">Profil introuvable</p>
      </div>
    );
  }

  const {
    username,
    description = '',
    amountGiven = 0,
  } = profileData || {};

  useEffect(() => {
    const saved = localStorage.getItem('donation_history');
    if (saved) {
      const history = JSON.parse(saved);
      const userDonations = history.filter((donation: any) => donation.userId === profileData.userId);
      const totalAmount = userDonations.reduce((sum: number, donation: any) => sum + donation.amount, 0);
      profileData.amountGiven = totalAmount;
    }
  }, [profileData.userId]);

  const [activeTab, setActiveTab] = useState<'donnes' | 'recus'>('donnes');
  const [showTransactions, setShowTransactions] = useState(false);

  const displayPhotos: Photo[] = React.useMemo(() => {
    if (!profileData?.externalPhotos || !Array.isArray(profileData.externalPhotos) || !profileData.showExternalPhotos) {
      return [];
    }

    if (profileData.externalPhotos.length > 0 && typeof profileData.externalPhotos[0] === 'string') {
      return (profileData.externalPhotos as string[]).map(url => ({
        url,
        title: '',
        description: ''
      }));
    }
    
    return (profileData.externalPhotos as any[]).map(photo => ({
      url: photo.url || '',
      title: photo.title || '',
      description: photo.description || ''
    }));
  }, [profileData?.externalPhotos, profileData?.showExternalPhotos]);

  const handleShare = () => {
    const shareUrl = `${window.location.origin}/profil/${profileData.userId}`;
    const shareText = `Découvrez le profil de ${username} sur Magraine`;

    if (navigator.share) {
      navigator.share({
        title: shareText,
        text: shareText,
        url: shareUrl,
      }).catch(() => setIsShareModalOpen(true));
    } else {
      setIsShareModalOpen(true);
    }
  };

  const handleProfileLike = async () => {
    if (!profileData?.userId) return;

    try {
      // Créer le document utilisateur s'il n'existe pas
      const userRef = doc(db, 'users', profileData.userId);
      const userDoc = await getDoc(userRef);
      
      if (!userDoc.exists()) {
        await setDoc(userRef, {
          likes: 0,
          userId: profileData.userId,
          username: profileData.username
        });
      }

      // Gérer le like
      const likeRef = doc(db, 'users', profileData.userId, 'profileLikes', user?.id || 'anonymous');
      const likeDoc = await getDoc(likeRef);
      const hasLiked = likeDoc.exists();

      if (!hasLiked) {
        await updateDoc(userRef, {
          likes: increment(1)
        });
        await setDoc(likeRef, {
          timestamp: new Date()
        });
        setTotalLikes(prev => prev + 1);
        setIsLiked(true);
      } else {
        await updateDoc(userRef, {
          likes: increment(-1)
        });
        await deleteDoc(likeRef);
        setTotalLikes(prev => prev - 1);
        setIsLiked(false);
      }
    } catch (error) {
      console.error('Erreur lors de la mise à jour du like:', error);
    }
  };

  const handleDonationClick = (requestId: string) => {
    navigate(`/2dr/request/${requestId}`);
  };

  const renderReceivedDonations = () => (
    <div className="space-y-4">
      {receivedDonations.length > 0 ? (
        receivedDonations.map((groupedDonation) => (
          <div
            key={groupedDonation.requestId}
            onClick={() => navigate(`/2dr/request/${groupedDonation.requestId}`)}
            className="bg-black/40 p-4 rounded-xl cursor-pointer hover:bg-black/50 transition-all border border-white/5 hover:border-white/10"
          >
            <h3 className="text-white font-semibold mb-2">
              {groupedDonation.requestTitle}
            </h3>
            <div className="flex justify-between items-center">
              <span className="text-gray-400 text-sm">
                {groupedDonation.donations.length} donation{groupedDonation.donations.length > 1 ? 's' : ''}
              </span>
              <span className="text-green-400 font-medium">
                {groupedDonation.totalAmount.toLocaleString()} {currency}
              </span>
            </div>
          </div>
        ))
      ) : (
        <p className="text-center text-gray-400">Aucune donation reçue</p>
      )}
    </div>
  );

  const socialLinks = profileData?.socialLinks || [];

  return (
    <div className="min-h-screen">
      {/* Header - Réduire le padding horizontal */}
      <div className="bg-black/50 text-white px-2 py-4 flex items-center justify-between sticky top-0 z-10 backdrop-blur-lg">
        <button
          onClick={() => navigate(-1)}
          className="text-gray-400 hover:text-white transition-colors"
        >
          <ArrowLeft className="w-6 h-6" />
        </button>
        <span className="text-white font-bold">Profil Public</span>
        <button
          onClick={handleShare}
          className="text-gray-400 hover:text-white transition-colors"
        >
          <Share2 className="w-5 h-5" />
        </button>
      </div>

      <div className="container mx-auto py-1 max-w-2xl">
        {/* Profile Info - Ajuster les marges négatives */}
        <div className="flex flex-col items-center mb-4 bg-black/20 p-4 pt-6 rounded-xl">
          <div className="mb-3">
            <img
              src={profileData?.profilePicture}
              alt={username}
              className="w-24 h-24 rounded-full object-cover"
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.src = generateInitialsAvatar(username);
              }}
            />
          </div>
          <div className="text-center pt-2">
            <div className="relative">
              <h1 className="text-xl font-bold text-white flex items-center justify-center gap-1">
                {username}
                <button
                  onClick={() => navigate(`/username-history/${profileData.userId}`)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <Info className="w-4 h-4" />
                </button>
              </h1>
              <div className="flex items-center justify-center gap-3 mt-3">
                <p className="text-green-400">
                  {formatAmount(amountGiven)} {currency}
                  <span className="text-gray-400 text-sm ml-1">Donations</span>
                </p>
                <button
                  onClick={handleProfileLike}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-xl ${
                    isLiked 
                      ? 'bg-pink-500/20 text-pink-400 hover:bg-pink-500/30' 
                      : 'bg-white/5 hover:bg-white/10 text-gray-300'
                  }`}
                >
                  <Heart className={`w-4 h-4 ${isLiked ? 'fill-current' : ''}`} />
                  <span className="text-sm">{totalLikes}</span>
                </button>
                {userRanking && (
                  <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-white/5 text-yellow-400">
                    <Trophy className="w-4 h-4" />
                    <span className="text-sm">#{userRanking}</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {description && (
          <p className="text-gray-400 mb-2 text-center whitespace-pre-line">
            {description.split('\n').map((line, i) => {
              const followKeywords = ['suivant', 'suivez', 'suivre'];
              const hasFollowWord = followKeywords.some(keyword => 
                line.toLowerCase().includes(keyword)
              );

              if (hasFollowWord && socialLinks.length > 0) {
                return (
                  <span key={i}>
                    {line}{' '}
                    {socialLinks.map((link: SocialLink, index: number) => (
                      <a
                        key={index}
                        href={link.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-green-400 hover:text-green-300 transition-colors inline-flex items-center gap-1"
                      >
                        {link.platform}
                        <ExternalLink className="w-3 h-3" />
                      </a>
                    ))}
                  </span>
                );
              }
              return <span key={i}>{line}{'\n'}</span>;
            })}
          </p>
        )}

        {/* Tabs */}
        <div className="border-b border-white/10 mb-6">
          <div className="flex border-b border-white/10">
            <div
              className="px-4 hover:text-white transition-colors cursor-pointer text-gray-400 flex items-center"
              onClick={() => setShowTransactions(!showTransactions)}
            >
              {showTransactions ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
            </div>
            <button
              className={`flex-1 py-3 text-center transition-colors ${
                activeTab === 'donnes'
                  ? 'text-green-400'
                  : 'text-gray-400 hover:text-white'
              }`}
              onClick={() => setActiveTab('donnes')}
            >
              Donnés
            </button>
            <button
              className={`flex-1 py-3 text-center transition-colors ${
                activeTab === 'recus'
                  ? 'text-green-400'
                  : 'text-gray-400 hover:text-white'
              }`}
              onClick={() => setActiveTab('recus')}
            >
              Reçus
            </button>
          </div>

          <div className="p-4 space-y-4">
            {showTransactions ? (
              activeTab === 'donnes' ? (
                <div className="space-y-4">
                  {donationNotifications.length > 0 && (
                    <div className="space-y-2">
                      {donationNotifications.map((notification) => (
                        <div
                          key={notification.id}
                          onClick={() => handleDonationClick(notification.requestId)}
                          className="bg-white/5 p-4 rounded-xl cursor-pointer hover:bg-white/10 transition-colors"
                        >
                          <h3 className="text-white font-bold">{notification.requestTitle}</h3>
                          <p className="text-green-400">
                            {notification.amount.toLocaleString()} {currency}
                          </p>
                          <p className="text-gray-400 text-sm">
                            {new Date(notification.date.toDate()).toLocaleDateString('fr-FR', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                  {sentDonations.map((donation) => (
                    <div
                      key={donation.id}
                      onClick={() => handleDonationClick(donation.requestId)}
                      className="border border-white/10 p-4 rounded-xl hover:border-white/20 transition-colors cursor-pointer"
                    >
                      <h3 className="text-white font-bold">{donation.requestTitle}</h3>
                      <p className="text-green-400">
                        {donation.amount.toLocaleString()} {currency}
                      </p>
                      <p className="text-gray-400 text-sm">
                        {typeof donation.date === 'string' 
                          ? new Date(donation.date).toLocaleDateString('fr-FR', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })
                          : 'Date non disponible'
                        }
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                renderReceivedDonations()
              )
            ) : (
              <p className="text-gray-400 text-center">Cliquez sur l'icône de l'œil pour voir</p>
            )}
          </div>
        </div>
        
        {/* Photos Carousel */}
        {displayPhotos.length > 0 && profileData.showExternalPhotos && (
          <div className="mt-2">
            <h2 className="text-xl font-bold text-white mb-4">Galerie photos</h2>
            <PhotoGallery photos={displayPhotos} />
          </div>
        )}
      </div>

      {/* Prompt de connexion/inscription pour les visiteurs non authentifiés */}
      {showAuthPrompt && (
        <div className="fixed bottom-0 left-0 right-0 bg-black/10 backdrop-blur-lg p-4 border-t border-white/1">
          <div className="max-w-md mx-auto">
            <p className="text-white text-center mb-4">
              Rejoignez magraine pour interagir avec {username} et découvrir plus de contenu !
            </p>
            <div className="flex gap-2">
              <button
                onClick={() => navigate('/login')}
                className="flex-1 bg-white/10 text-white py-2 rounded-lg"
              >
                Se connecter
              </button>
              <button
                onClick={() => navigate('/register')}
                className="flex-1 bg-green-500 text-white py-2 rounded-lg"
              >
                S'inscrire
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de partage */}
      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        url={`${window.location.origin}/profil/${profileData.userId}`}
      />
    </div>
  );
}